// src/globalManager/CacheManager.js

// --- CONFIGURATION ---
const DB_NAME = 'TvApp_CacheDB';
const DB_VERSION = 1;
const STORE_NAME = 'api_responses';
const MEMORY_CACHE = new Map(); // Level 1 Cache (RAM - Super Fast)

// --- INDEXED DB HELPERS (Standard Boilerplate) ---
const dbPromise = new Promise((resolve, reject) => {
  if (!window.indexedDB) {
    console.warn("IndexedDB not supported. Falling back to non-persistent cache.");
    resolve(null); // Fallback: code will run but won't save to disk
    return;
  }

  const request = window.indexedDB.open(DB_NAME, DB_VERSION);

  request.onupgradeneeded = (event) => {
    const db = event.target.result;
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME, { keyPath: 'key' });
    }
  };

  request.onsuccess = (event) => resolve(event.target.result);
  request.onerror = (event) => reject(event.target.error);
});

// --- CACHE API ---
export const apiCache = {
  // 1. GET DATA
  get: async function(key) {
    // A. Try Memory First (Fastest)
    if (MEMORY_CACHE.has(key)) {
      const item = MEMORY_CACHE.get(key);
      if (Date.now() < item.expiry) {
        // console.log(`[Cache] Hit Memory: ${key}`);
        return item.data;
      } else {
        MEMORY_CACHE.delete(key); // Expired
      }
    }

    // B. Try IndexedDB Second (Persistent)
    try {
      const db = await dbPromise;
      if (!db) return null;

      return new Promise((resolve) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const request = store.get(key);

        request.onsuccess = () => {
          const result = request.result;
          
          if (!result) {
            resolve(null); // Not found
            return;
          }

          // Check Expiry
          if (Date.now() > result.expiry) {
            // console.log(`[Cache] Expired in DB: ${key}`);
            this.delete(key); // Cleanup DB
            resolve(null);
          } else {
            // console.log(`[Cache] Hit DB: ${key}`);
            // RE-HYDRATE MEMORY: Save back to RAM for next time
            MEMORY_CACHE.set(key, result); 
            resolve(result.data);
          }
        };
        
        request.onerror = () => resolve(null);
      });
    } catch (e) {
      console.error("Cache Read Error", e);
      return null;
    }
  },

  // 2. SET DATA
  set: async function(key, data, duration = 1 * 60 * 1000) {
    const expiry = Date.now() + duration;
    const item = { key, data, expiry };

    // A. Save to Memory
    MEMORY_CACHE.set(key, item);

    // B. Save to IndexedDB
    try {
      const db = await dbPromise;
      if (db) {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.put(item);
      }
    } catch (e) {
      console.error("Cache Write Error", e);
    }
  },

  // 3. DELETE DATA
  delete: async function(key) {
    MEMORY_CACHE.delete(key);
    
    try {
      const db = await dbPromise;
      if (db) {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.delete(key);
      }
    } catch (e) { /* ignore */ }
  },

  // 4. CLEAR ALL
  clear: async function() {
    MEMORY_CACHE.clear();
    try {
      const db = await dbPromise;
      if (db) {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.clear();
      }
    } catch (e) { /* ignore */ }
  }
};

// --- EXPORTED HELPER ---
export async function fetchWithCache(key, fetchFn, duration = 30 * 60 * 1000) {
  // 1. Check Cache
  const cachedData = await apiCache.get(key);
  if (cachedData) {
    return cachedData;
  }

  // 2. Fetch Fresh Data
  // console.log(`[Cache] Fetching Network: ${key}`);
  try {
    const data = await fetchFn();
    
    // 3. Save to Cache if valid
    if (data) {
      // Don't await the set operation to keep UI snappy
      apiCache.set(key, data, duration); 
    }
    return data;
  } catch (error) {
    throw error;
  }
}